import { createFileRoute, Link } from "@tanstack/react-router";

import { LegalCallout, LegalDocument, LegalSection } from "@/features/legal/LegalDocument";
import { LEGAL_RESPONSIBLE } from "@/features/legal/legal-config";

export const Route = createFileRoute("/legal/reembolsos")({
  head: () => ({
    meta: [
      { title: "Política de reembolsos digitales | Socrattica" },
      {
        name: "description",
        content:
          "Retracto, garantía, reversión del pago y reembolsos para productos digitales de Socrattica.",
      },
    ],
  }),
  component: RefundsPage,
});

function RefundsPage() {
  return (
    <LegalDocument
      eyebrow="Política de reembolsos"
      title="Una ruta concreta si algo no sale bien."
      summary="Esta política distingue el retracto, los problemas de calidad, los pagos duplicados y la reversión del pago para que sepas qué pedir, cuándo y por qué canal."
    >
      <LegalCallout title="Canal único con trazabilidad">
        <p>
          Presenta la solicitud en el <Link to="/productos/tickets">centro de soporte</Link> o en{" "}
          <a href={`mailto:${LEGAL_RESPONSIBLE.email}`}>{LEGAL_RESPONSIBLE.email}</a>. Incluye
          correo de compra, número de pedido, producto, motivo y soportes. Nunca envíes el número
          completo ni el código de seguridad de tu tarjeta.
        </p>
      </LegalCallout>

      <LegalSection id="alcance" title="1. Alcance">
        <p>
          Aplica a guías, libros, audio, plantillas y demás recursos digitales vendidos directamente
          por Socrattica. Los derechos legales prevalecen sobre cualquier condición menos favorable.
        </p>
      </LegalSection>

      <LegalSection id="retracto" title="2. Derecho de retracto">
        <p>
          En las ventas a distancia puedes solicitar el retracto dentro de los cinco días hábiles
          siguientes a la celebración de la compra, en los casos en que legalmente proceda. Para
          facilitarlo, Socrattica aceptará la solicitud cuando el archivo no haya sido descargado ni
          se haya iniciado el acceso digital.
        </p>
        <p>
          Si autorizaste entrega inmediata y el acceso o descarga ya comenzó, revisaremos la
          procedencia conforme a la naturaleza del producto y las excepciones legales. Esta
          evaluación no elimina la garantía por falta de calidad, archivo defectuoso o contenido
          distinto al ofrecido.
        </p>
      </LegalSection>

      <LegalSection id="garantia" title="3. Archivo defectuoso o producto no conforme">
        <p>
          Avísanos si el archivo no abre, está incompleto, está corrupto, no corresponde al producto
          comprado o no incluye una característica esencial anunciada. Adjunta una descripción,
          captura del error y datos básicos del dispositivo o lector.
        </p>
        <p>
          Verificaremos el caso y, según corresponda, repararemos el archivo, entregaremos una copia
          funcional, reemplazaremos el recurso o devolveremos el dinero. No se considera defecto la
          falta de compatibilidad con un programa o equipo contrario a requisitos claramente
          informados antes de comprar.
        </p>
      </LegalSection>

      <LegalSection id="no-entrega" title="4. Pago aprobado sin entrega">
        <p>
          Si el pago está aprobado y el acceso no aparece, revisa primero el correo no deseado y
          entra con el mismo correo usado en la compra. Si transcurren 24 horas, presenta un ticket.
          Priorizaremos restablecer la entrega y, si no es posible dentro del plazo ofrecido o del
          máximo legal, podrás terminar la compra y pedir devolución total sin descuento.
        </p>
        <p>
          El botón privado permanece disponible desde la cuenta mientras la compra esté aprobada. Si
          una falla atribuible a Socrattica impide la descarga, revisaremos los registros y
          aplicaremos la corrección, reemplazo o devolución que corresponda. No tendrás que pagar
          otra vez para recibir la solución de una garantía procedente.
        </p>
      </LegalSection>

      <LegalSection id="duplicado" title="5. Pago duplicado o valor incorrecto">
        <p>
          Si se registró más de un cobro por el mismo pedido o un valor distinto al total
          confirmado, envía los comprobantes donde se vean fecha, valor y referencia, ocultando
          datos sensibles. Una vez verificado, devolveremos el excedente o cobro duplicado.
        </p>
      </LegalSection>

      <LegalSection id="reversion" title="6. Reversión del pago electrónico">
        <p>
          Cuando aplique la Ley 1480 de 2011, puedes solicitar la reversión si la operación fue
          fraudulenta o no solicitada, el producto no fue recibido, no correspondía a lo pedido o
          era defectuoso. Debes presentar la queja a Socrattica y notificar al emisor del
          instrumento de pago dentro de los cinco días hábiles siguientes a conocer el hecho o a la
          fecha en que debiste recibir el producto.
        </p>
        <p>
          La reversión es distinta de un reembolso comercial y participan la pasarela, el banco y
          los demás actores del pago.
        </p>
      </LegalSection>

      <LegalSection id="exclusiones" title="7. Casos que por sí solos no generan devolución">
        <ul>
          <li>
            Cambio de opinión después de descargar o usar el archivo, cuando no proceda retracto.
          </li>
          <li>Resultados académicos distintos a expectativas no prometidas en la ficha.</li>
          <li>
            Compra del nivel equivocado cuando la ficha lo informó claramente y el archivo se usó.
          </li>
          <li>
            Problemas causados por modificar el archivo o usar software incompatible no admitido.
          </li>
          <li>Solicitudes que impliquen abuso, fraude o una compra que no pueda verificarse.</li>
        </ul>
        <p>
          Evaluaremos cada caso de buena fe y estas exclusiones nunca desplazan una garantía o
          derecho obligatorio.
        </p>
      </LegalSection>

      <LegalSection id="proceso" title="8. Proceso de solicitud">
        <ol className="ml-5 list-decimal space-y-2">
          <li>Identifica el pedido, correo, producto y causal.</li>
          <li>Adjunta comprobantes o capturas necesarias, sin datos financieros completos.</li>
          <li>Indica si prefieres el mismo medio de pago u otra opción legalmente disponible.</li>
          <li>Recibirás una referencia de soporte para consultar el estado.</li>
          <li>
            Podemos pedir información necesaria para validar identidad, pago o problema técnico.
          </li>
        </ol>
      </LegalSection>

      <LegalSection id="plazos" title="9. Respuesta y devolución del dinero">
        <p>
          Nuestro objetivo de primera respuesta es de {LEGAL_RESPONSIBLE.responseTime}. Cuando se
          apruebe un retracto de comercio electrónico y hayas entregado la información completa y
          cumplido las obligaciones aplicables, la devolución se realizará en máximo quince días
          calendario. El mismo máximo aplica a devoluciones por falta de disponibilidad o
          incumplimiento del plazo de entrega en los casos previstos por la ley.
        </p>
        <p>
          La acreditación visible puede depender de tiempos bancarios. Te informaremos el medio
          utilizado y conservaremos la trazabilidad del caso.
        </p>
      </LegalSection>
    </LegalDocument>
  );
}
